import { AxiosResponse } from 'axios'
import BaseTableCard from 'common/components/BaseTableCard'
import BasicTable from 'common/components/BasicTable'
import { Column } from 'common/components/BasicTable/types'
import { DateRange } from 'common/components/DateRangePicker'
import useDeleteConfirmDialog from 'common/components/useDeleteConfirmDialog'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useAnalytics, { AnalyticsEvents } from 'common/hooks/useAnalytics'
import { formatDate, formatThaiShortDateTwoDigitYear } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import parseISO from 'date-fns/parseISO'
import capitalize from 'lodash/capitalize'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useSpeciesName } from 'modules/pond/api/selector'
import MoreAction from 'modules/pond/components/MoreAction'
import { useAllowFeatures, useGetCRUDByFeature } from 'modules/user/api/selector'
import { useMutateDeleteWaterQuality } from 'modules/water/api/mutation'
import {
  useGetWaterChart,
  useMutateDeleteWater,
  useWaterEditDialog,
  useWaterTable,
} from 'modules/water/api/selector'
import {
  QualityTableRow,
  Vibrio,
  WaterEditData,
  WaterMineral,
  WaterQuality,
  WaterTableFilter,
  isWaterQuality,
} from 'modules/water/api/types'
import { MINERALS, PeriodOfDay, VIBRIOS, WATER_QUALITY_OPTIONS } from 'modules/water/constants'
import {
  waterDateTableFilterState,
  selectedWaterIdState,
  waterTableFilterState,
  selectedWaterQualityDateState,
} from 'modules/water/stores/atom'
import { prepareQualityRows } from 'modules/water/utils/water'
import { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'
import { normalizeSpeciesName } from 'utils/analytics'

import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'

interface WaterTableProps {
  refetchToday?: () => void
}

const WaterTable = ({ refetchToday }: WaterTableProps) => {
  const { t } = useTranslation('water')
  const [filter, setFilter] = useRecoilState(waterTableFilterState)

  const mutation = useMutateDeleteWater()
  const queryChart = useGetWaterChart()
  const WaterDialog = useWaterEditDialog()
  const speciesName = useSpeciesName()

  const deleteMutation = mutation()
  const deleteQualityMutation = useMutateDeleteWaterQuality()
  const allowFeatures = useAllowFeatures()
  const { trackEvent } = useAnalytics()
  const cruds = useGetCRUDByFeature(filter)
  const canEdit = cruds?.[CRUDAction.Update] === 1

  // State
  const [waterId, setWaterId] = useRecoilState(selectedWaterIdState)
  const [qualityDate, setQualityDate] = useRecoilState(selectedWaterQualityDateState)
  const [selectedRow, setSelectedRow] = useState<WaterEditData | null>(null)
  const [open, setOpen] = useState(false)
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: null, endDate: null })

  // Pass dateRange to hook
  const waterTableHook = useWaterTable({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
  })

  const { data = [], isFetching, refetch, fetchNextPage, hasNextPage } = waterTableHook('table')
  const { refetch: refetchCard } = waterTableHook('card')
  const { refetch: refetchChart } = queryChart()

  // Success callback
  const onSuccess = (res: AxiosResponse<any>) => {
    const { status } = res.data
    if (status === 200) {
      setTimeout(() => {
        if (refetchToday) refetchToday()
        refetch()
        refetchCard()
        refetchChart()
      }, 1000)
      const waterRecordType =
        filter === FeatureKey.WaterQuality
          ? 'quality'
          : filter === FeatureKey.WaterMineral
          ? 'mineral'
          : 'vibrio'
      const normalizedSpecies = normalizeSpeciesName(speciesName)
      trackEvent(AnalyticsEvents.waterQualityRecordDeleted, {
        water_record_type: waterRecordType,
        ...(normalizedSpecies ? { species: normalizedSpecies } : {}),
      })
    }
  }

  // Delete confirm dialog
  const { dialog, openDialog: openConfirmDialog } = useDeleteConfirmDialog({
    onConfirm: async () => {
      if (filter === FeatureKey.WaterQuality) {
        if (!qualityDate) return
        const date = parseISO(qualityDate)
        const formattedDate = formatDate(date)
        await deleteQualityMutation.mutateAsync({ params: { date: formattedDate } }, { onSuccess })
      } else {
        await deleteMutation.mutateAsync(Number(waterId), { onSuccess })
      }
    },
  })

  // Filter select
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFilter(e.target.value as WaterTableFilter)
  }

  const groupColumns = WATER_QUALITY_OPTIONS.map((el) => ({
    id: el.category,
    title: `${t(el.label)} ${el.unit ? `(${t(el.unit)})` : ''}`,
    colSpan: 2,
    width: '200px',
  }))

  const qualityGroupColumns = [
    {
      row: 1,
      columns: [
        {
          id: '',
          colSpan: 2,
          width: '250px',
        },
        ...groupColumns,
        {
          id: '',
          colSpan: 1,
          width: '100px',
        },
      ],
    },
  ]

  const baseColumns = [
    {
      id: 'date',
      title: t('date'),
      format: formatThaiShortDateTwoDigitYear,
      width: '150px',
    },
    {
      id: 'age',
      title: <Trans i18nKey={'pond:animal.age'} values={{ species: speciesName }} />,
      format: (val: number) => fNumber(val, { disabledDecimal: true }),
      width: '100px',
    },
  ]

  const getQualityColumns = () => {
    const result = WATER_QUALITY_OPTIONS.map((el) =>
      Object.values(PeriodOfDay).map((time) => ({
        id: `${el.category}${capitalize(time)}`,
        title: t(time),
        format: fNumber,
        width: '100px',
      }))
    ).flat()

    return result
  }

  const getMineralColumns = () => {
    const result = MINERALS.map((el) => ({
      id: el.name,
      format: fNumber,
      title: `${t(el.label)} ${el.unit ? `(${t(el.unit)})` : ''}`,
      width: '100px',
    }))
    return result
  }

  const getVibrioColumns = () => {
    const result = VIBRIOS.map((el) => ({
      id: el.name,
      format: fNumber,
      title: `${t(el.label)} ${el.unit ? `(${t(el.unit)})` : ''}`,
      width: '100px',
    }))
    return result
  }

  const getColumns = () => {
    const columns: Column[] = [...baseColumns]
    const action = {
      id: 'action',
      title: '',
      width: '100px',
      preventRowClick: true,
    }

    switch (filter) {
      case FeatureKey.WaterMineral:
        const mineralColumns = getMineralColumns()
        columns.push(...mineralColumns)
        columns.push(action)
        break
      case FeatureKey.WaterVibrio:
        const vibrioColumns = getVibrioColumns()
        columns.push(...vibrioColumns)
        columns.push(action)
        break
      case FeatureKey.WaterQuality:
      default:
        const qualityColumns = getQualityColumns()
        columns.push(...qualityColumns)
        columns.push(action)
    }
    return columns
  }

  const getPreparedQualityRows = () => {
    const sample = data[0]
    if (isWaterQuality(sample)) {
      return prepareQualityRows(data as WaterQuality[])
    }
    return []
  }

  const handleRefetch = () => {
    refetch()
    refetchCard()
    refetchChart()
    if (refetchToday) refetchToday()
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpenEdit = (row: Vibrio | WaterMineral | (QualityTableRow & { id: number })) => {
    setQualityDate(row.date)
    setWaterId(row.id)
    setSelectedRow(row)
    setOpen(true)
  }

  const handleOpenDeleteConfirm = (id: number, date: string) => {
    setQualityDate(date)
    setWaterId(id)
    openConfirmDialog()
  }

  const createTableRow = (row: Vibrio | WaterMineral | (QualityTableRow & { id: number })) => ({
    ...row,
    age: row.shrimpAge,
    action: (
      <MoreAction
        featureKey={filter}
        isHideOnCompleted={false}
        onClickEdit={() => handleOpenEdit(row)}
        onClickDelete={() => handleOpenDeleteConfirm(row.id, row.date)}
      />
    ),
  })

  const getRows = () => {
    switch (filter) {
      case FeatureKey.WaterMineral:
        return data as WaterMineral[]
      case FeatureKey.WaterVibrio:
        return data as Vibrio[]
      case FeatureKey.WaterQuality:
      default:
        return getPreparedQualityRows()
    }
  }

  const rows = getRows().map((row) => createTableRow(row as QualityTableRow & { id: number }))
  const onLoadMoreRows = () => {
    if (hasNextPage) {
      fetchNextPage()
    }
  }

  const WATER_FILTER_MENUS = [
    { key: FeatureKey.WaterQuality, label: t('water.quality') },
    { key: FeatureKey.WaterMineral, label: t('mineral.in.water') },
    { key: FeatureKey.WaterVibrio, label: t('bacteria.in.water') },
  ]
  const menuItems = useMemo(() => {
    const featureKeys = allowFeatures.map((el) => el.feature.key)
    return WATER_FILTER_MENUS.filter((el) => featureKeys.includes(el.key))
  }, [allowFeatures])

  useEffect(() => {
    if (menuItems.length === 0) return

    const allowedKeys = menuItems.map((m) => m.key)

    if (!allowedKeys.includes(filter)) {
      setFilter(allowedKeys[0] as WaterTableFilter)
    }
  }, [menuItems, filter])
  return (
    <>
      {dialog}
      <BaseTableCard
        title={t('water.overview.summary.table')}
        refresh={refetch}
        isLoading={isFetching}
        isEmpty={data.length === 0}
        dateRecoilState={waterDateTableFilterState}
        isDateRangePicker
        dateRange={dateRange}
        onDateRangeChange={(range) => setDateRange(range)}
        displayValue={dashboardDateDisplayValue}
        moreFilter={
          <TextField
            select
            sx={{ width: 150 }}
            name="waterFilter"
            label={t('data.set')}
            size="medium"
            value={filter}
            onChange={handleChange}
          >
            {menuItems.map((item) => (
              <MenuItem key={item.key} value={item.key}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        }
      >
        <BasicTable
          columns={getColumns()}
          groupColumns={filter === FeatureKey.WaterQuality ? qualityGroupColumns : undefined}
          onLoading={onLoadMoreRows}
          isLoading={isFetching}
          rows={rows}
          onRowClick={canEdit ? (row) => handleOpenEdit(row) : undefined}
        />
      </BaseTableCard>
      <WaterDialog
        onClose={handleClose}
        open={open}
        isEdit
        onSuccessSave={handleRefetch}
        editData={selectedRow ?? undefined}
      />
    </>
  )
}

export default WaterTable
